import axios from 'utils/axios';
import type {
  Hotel,
  Event,
  EventRoom,
  EventRoomBooking,
  EventRoomLayout,
  EventRoomPrice,
  HotelImage,
  Service,
  Person,
  Partner,
  PartnerContact,
  EventAttendee,
  EventOrganizer,
  EventOrganizerRole,
  EmailSeverity,
  EventNotification,
  EventChangelogEntry,
  LayoutType,
  HotelRoomType,
  HotelRoomTypeImage,
  EventHotelRoomBooking,
  EventRoomImage,
  EventRoomLayoutImage,
  EventOffer,
  EventOfferRequest,
  OfferLineItem,
  OfferRequestStatus,
  PublicOfferView,
  EventAgendaItem,
  EventDispoNote,
  EventDispoSummary
} from 'types/r2m';

const dateToString = (d: Date) => d.toISOString().slice(0, 10);

export async function getCurrencies(): Promise<{ code: string }[]> {
  const res = await axios.get<{ code: string }[]>('/currencies');
  return Array.isArray(res.data) ? res.data : [];
}

export async function getAllowedHotels(date?: Date): Promise<Hotel[]> {
  const d = date ?? new Date();
  const res = await axios.get<Hotel[]>('/permission/allowed-hotel', {
    params: { date: dateToString(d) }
  });
  return res.data ?? [];
}

export async function setSelectedHotel(hotelId: number): Promise<void> {
  await axios.post('/auth/session/selected-hotel', { hotelId });
}

export async function getEventRooms(hotelId: number): Promise<EventRoom[]> {
  const res = await axios.get<EventRoom[]>('/event-room', {
    params: { hotelId }
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function getEventRoom(id: number): Promise<EventRoom> {
  const res = await axios.get<EventRoom>(`/event-room/${id}`);
  return res.data;
}

export async function createEventRoom(body: {
  hotelId: number;
  parentId?: number | null;
  translations: { languageCode: string; name: string; description?: string }[];
}): Promise<EventRoom> {
  const res = await axios.post<EventRoom>('/event-room', body);
  return res.data;
}

export async function updateEventRoom(
  id: number,
  body: Partial<{
    hotelId: number;
    parentId: number | null;
    translations: { languageCode: string; name: string; description?: string }[];
  }>
): Promise<EventRoom> {
  const res = await axios.put<EventRoom>(`/event-room/${id}`, body);
  return res.data;
}

export async function deactivateEventRoom(id: number): Promise<EventRoom> {
  const res = await axios.post<EventRoom>(`/event-room/${id}/deactivate`);
  return res.data;
}

export async function getHotelBookings(
  hotelId: number,
  dateFrom: string,
  dateTo: string,
  eventId?: number
): Promise<{ rooms: EventRoom[]; bookings: EventRoomBooking[]; event?: Event }> {
  const params: Record<string, string | number> = { dateFrom, dateTo };
  if (eventId) params.eventId = eventId;
  const res = await axios.get<{ rooms: EventRoom[]; bookings: EventRoomBooking[]; event?: Event }>(
    `/hotel/${hotelId}/bookings`,
    { params }
  );
  return res.data ?? { rooms: [], bookings: [] };
}

export async function getHotelEvents(
  hotelId: number,
  params?: { from?: string; to?: string; status?: string; q?: string }
): Promise<Event[]> {
  const res = await axios.get<Event[]>(`/hotel/${hotelId}/events`, { params });
  return Array.isArray(res.data) ? res.data : [];
}

export async function createHotelEvent(
  hotelId: number,
  body: {
    name: string;
    partnerId?: number;
    status?: string;
    realizationChance?: number | null;
    startAt: string;
    endAt: string;
    expectedAttendees?: number | null;
    notes?: string | null;
  }
): Promise<Event> {
  const res = await axios.post<Event>(`/hotel/${hotelId}/events`, body);
  return res.data;
}

export async function getHotelEvent(hotelId: number, id: number): Promise<Event> {
  const res = await axios.get<Event>(`/hotel/${hotelId}/events/${id}`);
  return res.data;
}

export async function getEvent(id: number): Promise<Event> {
  const res = await axios.get<Event>(`/events/${id}`);
  return res.data;
}

export async function updateHotelEvent(
  hotelId: number,
  id: number,
  body: Partial<{
    name: string;
    partnerId: number | null;
    status: string;
    realizationChance: number | null;
    startAt: string;
    endAt: string;
    expectedAttendees: number | null;
    notes: string | null;
  }>
): Promise<Event> {
  const res = await axios.patch<Event>(`/hotel/${hotelId}/events/${id}`, body);
  return res.data;
}

export async function deleteHotelEvent(hotelId: number, id: number): Promise<void> {
  await axios.delete(`/hotel/${hotelId}/events/${id}`);
}

export async function getPersons(hotelId: number, query?: string): Promise<Person[]> {
  const res = await axios.get<Person[]>('/persons', {
    params: { hotelId, query: query || undefined, limit: 20 }
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function createPerson(
  hotelId: number,
  body: { firstName: string; lastName: string; email?: string | null; phone?: string | null }
): Promise<{ id: number }> {
  const res = await axios.post<{ id: number }>('/persons', { hotelId, ...body });
  return res.data;
}

export async function getHotelPartners(hotelId: number, q?: string): Promise<Partner[]> {
  const res = await axios.get<Partner[]>(`/hotel/${hotelId}/partners`, {
    params: q ? { q } : undefined
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function createHotelPartner(
  hotelId: number,
  body: { name: string; vatNumber?: string | null; notes?: string | null }
): Promise<Partner> {
  const res = await axios.post<Partner>(`/hotel/${hotelId}/partners`, body);
  return res.data;
}

export async function updatePartner(
  id: number,
  body: Partial<{ name: string; vatNumber: string | null; notes: string | null }>
): Promise<Partner> {
  const res = await axios.patch<Partner>(`/partners/${id}`, body);
  return res.data;
}

export async function deletePartner(id: number): Promise<void> {
  await axios.delete(`/partners/${id}`);
}

export async function getPartnerContacts(partnerId: number): Promise<PartnerContact[]> {
  const res = await axios.get<PartnerContact[]>(`/partners/${partnerId}/contacts`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createPartnerContact(
  partnerId: number,
  body: {
    person?: { firstName: string; lastName: string; email?: string | null; phone?: string | null };
    personId?: number;
    roleTitle?: string | null;
    isPrimary?: boolean;
  }
): Promise<PartnerContact> {
  const res = await axios.post<PartnerContact>(`/partners/${partnerId}/contacts`, body);
  return res.data;
}

export async function updatePartnerContact(
  id: number,
  body: Partial<{ roleTitle: string | null; isPrimary: boolean }>
): Promise<PartnerContact> {
  const res = await axios.patch<PartnerContact>(`/partner-contacts/${id}`, body);
  return res.data;
}

export async function deletePartnerContact(id: number): Promise<void> {
  await axios.delete(`/partner-contacts/${id}`);
}

export async function getEventAttendees(eventId: number): Promise<EventAttendee[]> {
  const res = await axios.get<EventAttendee[]>(`/events/${eventId}/attendees`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function getEventOrganizers(eventId: number): Promise<EventOrganizer[]> {
  const res = await axios.get<EventOrganizer[]>(`/events/${eventId}/organizers`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventOrganizer(
  eventId: number,
  body: {
    person?: { firstName: string; lastName: string; email?: string | null; phone?: string | null };
    personId?: number;
    roleTitle?: string | null;
    primaryRole?: EventOrganizerRole;
    roles?: EventOrganizerRole[];
    userId?: number | null;
    notify?: boolean;
    minSeverityEmail?: EmailSeverity;
  }
): Promise<EventOrganizer> {
  const res = await axios.post<EventOrganizer>(`/events/${eventId}/organizers`, body);
  return res.data;
}

export async function updateEventOrganizer(
  id: number,
  body: Partial<{
    roleTitle: string | null;
    primaryRole: EventOrganizerRole;
    roles: EventOrganizerRole[];
    userId: number | null;
    notify: boolean;
    minSeverityEmail: EmailSeverity;
  }>
): Promise<EventOrganizer> {
  const res = await axios.patch<EventOrganizer>(`/event-organizers/${id}`, body);
  return res.data;
}

export async function deleteEventOrganizer(id: number): Promise<void> {
  await axios.delete(`/event-organizers/${id}`);
}

export async function getEventChangelog(eventId: number): Promise<EventChangelogEntry[]> {
  const res = await axios.get<EventChangelogEntry[]>(`/events/${eventId}/changelog`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventAttendee(
  eventId: number,
  body: {
    person?: { firstName: string; lastName: string; email?: string | null; phone?: string | null };
    personId?: number;
    attendeeType: string;
    status?: string;
    note?: string | null;
  }
): Promise<EventAttendee> {
  const res = await axios.post<EventAttendee>(`/events/${eventId}/attendees`, body);
  return res.data;
}

export async function updateEventAttendee(
  id: number,
  body: Partial<{ attendeeType: string; status: string; note: string | null }>
): Promise<EventAttendee> {
  const res = await axios.patch<EventAttendee>(`/event-attendees/${id}`, body);
  return res.data;
}

export async function deleteEventAttendee(id: number): Promise<void> {
  await axios.delete(`/event-attendees/${id}`);
}

export async function createHotelRoomBooking(
  hotelId: number,
  body: {
    eventRoomId: number;
    date: string;
    startTime: string;
    endTime: string;
    type?: string;
    title?: string;
    note?: string;
    eventId?: number;
  }
): Promise<EventRoomBooking> {
  const res = await axios.post<EventRoomBooking>(`/hotel/${hotelId}/bookings`, body);
  return res.data;
}

export async function getEventRoomBookings(
  eventRoomId: number,
  date?: Date | string
): Promise<EventRoomBooking[]> {
  const params: Record<string, string> = {};
  if (date) {
    params.date = typeof date === 'string' ? date : dateToString(date);
  }
  const res = await axios.get<EventRoomBooking[]>(
    `/event-room/${eventRoomId}/bookings`,
    { params }
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function getEventRoomBooking(
  eventRoomId: number,
  id: number
): Promise<EventRoomBooking> {
  const res = await axios.get<EventRoomBooking>(
    `/event-room/${eventRoomId}/bookings/${id}`
  );
  return res.data;
}

export async function createEventRoomBooking(
  eventRoomId: number,
  body: {
    date: string;
    startTime: string;
    endTime: string;
    type?: string;
    title?: string;
    note?: string;
    eventId?: number;
  }
): Promise<EventRoomBooking> {
  const res = await axios.post<EventRoomBooking>(
    `/event-room/${eventRoomId}/bookings`,
    body
  );
  return res.data;
}

export async function updateEventRoomBooking(
  eventRoomId: number,
  id: number,
  body: Partial<{
    eventRoomId: number;
    date: string;
    startTime: string;
    endTime: string;
    type: string;
    title: string;
    note: string;
  }>
): Promise<EventRoomBooking> {
  const res = await axios.put<EventRoomBooking>(
    `/event-room/${eventRoomId}/bookings/${id}`,
    body
  );
  return res.data;
}

export async function deleteEventRoomBooking(
  eventRoomId: number,
  id: number
): Promise<void> {
  await axios.delete(`/event-room/${eventRoomId}/bookings/${id}`);
}

export async function getHotelImages(hotelId: number): Promise<HotelImage[]> {
  const res = await axios.get<HotelImage[]>(`/hotel/${hotelId}/images`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function getHotelImage(
  hotelId: number,
  id: number
): Promise<HotelImage> {
  const res = await axios.get<HotelImage>(`/hotel/${hotelId}/images/${id}`);
  return res.data;
}

export async function getHotelServices(hotelId: number): Promise<Service[]> {
  const res = await axios.get<Service[]>(`/hotel/${hotelId}/services`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function getHotelService(
  hotelId: number,
  id: number
): Promise<Service> {
  const res = await axios.get<Service>(`/hotel/${hotelId}/services/${id}`);
  return res.data;
}

export async function getLayoutTypes(): Promise<LayoutType[]> {
  const res = await axios.get<LayoutType[]>('/layout-type');
  return Array.isArray(res.data) ? res.data : [];
}

export async function getEventRoomLayouts(eventRoomId: number): Promise<EventRoomLayout[]> {
  const res = await axios.get<EventRoomLayout[]>(`/event-room/${eventRoomId}/layouts`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventRoomLayout(
  eventRoomId: number,
  body: { layoutTypeId: number; capacity: number }
): Promise<EventRoomLayout> {
  const res = await axios.post<EventRoomLayout>(`/event-room/${eventRoomId}/layouts`, body);
  return res.data;
}

export async function updateEventRoomLayout(
  eventRoomId: number,
  id: number,
  body: Partial<{ layoutTypeId: number; capacity: number }>
): Promise<EventRoomLayout> {
  const res = await axios.put<EventRoomLayout>(`/event-room/${eventRoomId}/layouts/${id}`, body);
  return res.data;
}

export async function deleteEventRoomLayout(eventRoomId: number, id: number): Promise<void> {
  await axios.delete(`/event-room/${eventRoomId}/layouts/${id}`);
}

export async function createEventRoomPrice(
  params: {
    hotelId: number;
    eventRoomId: number;
    currencyId: string;
    dateFrom?: string;
    dateTo: string;
    price: number;
  }
): Promise<EventRoomPrice> {
  const { hotelId, eventRoomId, currencyId, dateFrom, dateTo, price } = params;
  const res = await axios.post<EventRoomPrice>(
    '/event-room-price',
    { eventRoomId, currencyId, dateFrom, dateTo, price },
    { params: { hotelId } }
  );
  return res.data;
}

export async function updateEventRoomPrice(
  params: {
    hotelId: number;
    id: number;
    eventRoomId: number;
    currencyId: string;
    dateFrom?: string;
    dateTo: string;
    price: number;
  }
): Promise<EventRoomPrice> {
  const { hotelId, id, eventRoomId, currencyId, dateFrom, dateTo, price } = params;
  const res = await axios.put<EventRoomPrice>(
    `/event-room-price/${id}`,
    { eventRoomId, currencyId, dateFrom, dateTo, price },
    { params: { hotelId } }
  );
  return res.data;
}

export async function deleteEventRoomPrice(hotelId: number, id: number): Promise<void> {
  await axios.delete(`/event-room-price/${id}`, { params: { hotelId } });
}

export async function getHotelRoomTypes(hotelId: number): Promise<HotelRoomType[]> {
  const res = await axios.get<HotelRoomType[]>(`/hotel/${hotelId}/room-types`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function getEventHotelRoomBookings(eventId: number): Promise<EventHotelRoomBooking[]> {
  const res = await axios.get<EventHotelRoomBooking[]>(`/events/${eventId}/hotel-room-bookings`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventHotelRoomBooking(
  eventId: number,
  body: {
    hotelRoomTypeId: number;
    dateFrom: string;
    dateTo: string;
    roomCount: number;
    personsPerRoom: number;
    price?: number | null;
    currencyId?: string | null;
    note?: string | null;
  }
): Promise<EventHotelRoomBooking> {
  const res = await axios.post<EventHotelRoomBooking>(`/events/${eventId}/hotel-room-bookings`, body);
  return res.data;
}

export async function updateEventHotelRoomBooking(
  eventId: number,
  id: number,
  body: Partial<{
    hotelRoomTypeId: number;
    dateFrom: string;
    dateTo: string;
    roomCount: number;
    personsPerRoom: number;
    price: number | null;
    currencyId: string | null;
    note: string | null;
  }>
): Promise<EventHotelRoomBooking> {
  const res = await axios.put<EventHotelRoomBooking>(`/events/${eventId}/hotel-room-bookings/${id}`, body);
  return res.data;
}

export async function deleteEventHotelRoomBooking(eventId: number, id: number): Promise<void> {
  await axios.delete(`/events/${eventId}/hotel-room-bookings/${id}`);
}

export async function getEventRoomImages(eventRoomId: number): Promise<EventRoomImage[]> {
  const res = await axios.get<EventRoomImage[]>(`/event-room/${eventRoomId}/images`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventRoomImage(
  eventRoomId: number,
  body: { imageUrl: string; default?: boolean }
): Promise<EventRoomImage> {
  const res = await axios.post<EventRoomImage>(`/event-room/${eventRoomId}/images`, body);
  return res.data;
}

export async function updateEventRoomImage(
  eventRoomId: number,
  id: number,
  body: Partial<{ imageUrl: string; default: boolean }>
): Promise<EventRoomImage> {
  const res = await axios.put<EventRoomImage>(`/event-room/${eventRoomId}/images/${id}`, body);
  return res.data;
}

export async function deleteEventRoomImage(eventRoomId: number, id: number): Promise<void> {
  await axios.delete(`/event-room/${eventRoomId}/images/${id}`);
}

export async function getEventRoomLayoutImages(
  eventRoomId: number,
  layoutId: number
): Promise<EventRoomLayoutImage[]> {
  const res = await axios.get<EventRoomLayoutImage[]>(
    `/event-room/${eventRoomId}/layouts/${layoutId}/images`
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventRoomLayoutImage(
  eventRoomId: number,
  layoutId: number,
  body: { imageUrl: string; default?: boolean }
): Promise<EventRoomLayoutImage> {
  const res = await axios.post<EventRoomLayoutImage>(
    `/event-room/${eventRoomId}/layouts/${layoutId}/images`,
    body
  );
  return res.data;
}

export async function updateEventRoomLayoutImage(
  eventRoomId: number,
  layoutId: number,
  id: number,
  body: Partial<{ imageUrl: string; default: boolean }>
): Promise<EventRoomLayoutImage> {
  const res = await axios.put<EventRoomLayoutImage>(
    `/event-room/${eventRoomId}/layouts/${layoutId}/images/${id}`,
    body
  );
  return res.data;
}

export async function deleteEventRoomLayoutImage(
  eventRoomId: number,
  layoutId: number,
  id: number
): Promise<void> {
  await axios.delete(`/event-room/${eventRoomId}/layouts/${layoutId}/images/${id}`);
}

export async function getMyNotifications(params?: {
  limit?: number;
  unreadOnly?: boolean;
}): Promise<EventNotification[]> {
  const res = await axios.get<EventNotification[]>('/my/notifications', {
    params: { limit: params?.limit ?? 20, unreadOnly: params?.unreadOnly ?? false }
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function getMyUnreadNotificationCount(): Promise<number> {
  const res = await axios.get<{ count: number }>('/my/notifications/unread-count');
  return res.data?.count ?? 0;
}

export async function markNotificationsRead(ids: number[]): Promise<{ affected: number }> {
  const res = await axios.post<{ affected: number }>('/my/notifications/mark-read', { ids });
  return res.data;
}

export async function markAllNotificationsRead(): Promise<{ affected: number }> {
  const res = await axios.post<{ affected: number }>('/my/notifications/mark-all-read');
  return res.data;
}

// ---- Public (unauthenticated) offer lifecycle ----

export async function submitOfferRequest(
  hotelSlug: string,
  body: {
    companyName: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    requestedStartAt: string;
    requestedEndAt: string;
    expectedAttendees: number;
    description: string;
    acceptedTerms: boolean;
    companyNameSecondary?: string;
  }
): Promise<{ status: string }> {
  const res = await axios.post<{ status: string }>(
    `/public/hotel/${encodeURIComponent(hotelSlug)}/offer-requests`,
    body
  );
  return res.data;
}

export async function getOfferByToken(token: string): Promise<PublicOfferView> {
  const res = await axios.get<PublicOfferView>(`/public/offer/${encodeURIComponent(token)}`);
  return res.data;
}

export async function respondToOffer(
  token: string,
  body: { action: 'accept' | 'reject' | 'change_request'; changeRequestReason?: string }
): Promise<{ status: string; registrationUrl?: string | null }> {
  const res = await axios.post<{ status: string; registrationUrl?: string | null }>(
    `/public/offer/${encodeURIComponent(token)}/respond`,
    body
  );
  return res.data;
}

export async function getRegistrationInfo(token: string): Promise<{
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  alreadyActivated: boolean;
}> {
  const res = await axios.get(`/public/register/${encodeURIComponent(token)}`);
  return res.data;
}

export async function completeRegistration(
  token: string,
  body: { password: string }
): Promise<{ id: number; email: string }> {
  const res = await axios.post(`/public/register/${encodeURIComponent(token)}/complete`, body);
  return res.data;
}

// ---- Hotel-side offer request inbox ----

export async function listOfferRequests(
  hotelId: number,
  status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending'
): Promise<EventOfferRequest[]> {
  const params: Record<string, string> = {};
  if (status !== 'all') params.status = status;
  const res = await axios.get<EventOfferRequest[]>(`/hotel/${hotelId}/offer-requests`, { params });
  return Array.isArray(res.data) ? res.data : [];
}

export async function getOfferRequest(
  hotelId: number,
  id: number
): Promise<EventOfferRequest> {
  const res = await axios.get<EventOfferRequest>(`/hotel/${hotelId}/offer-requests/${id}`);
  return res.data;
}

export async function approveOfferRequest(
  hotelId: number,
  id: number
): Promise<EventOfferRequest> {
  const res = await axios.post<EventOfferRequest>(
    `/hotel/${hotelId}/offer-requests/${id}/approve`
  );
  return res.data;
}

export async function rejectOfferRequest(
  hotelId: number,
  id: number,
  rejectionReason: string
): Promise<EventOfferRequest> {
  const res = await axios.post<EventOfferRequest>(
    `/hotel/${hotelId}/offer-requests/${id}/reject`,
    { rejectionReason }
  );
  return res.data;
}

// ---- Event-scoped offers ----

export async function listOffers(eventId: number): Promise<EventOffer[]> {
  const res = await axios.get<EventOffer[]>(`/events/${eventId}/offers`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function getOffer(eventId: number, id: number): Promise<EventOffer> {
  const res = await axios.get<EventOffer>(`/events/${eventId}/offers/${id}`);
  return res.data;
}

export async function createOffer(
  eventId: number,
  body: {
    validUntil?: string;
    currency?: string;
    notesForPartner?: string | null;
    internalNotes?: string | null;
    lineItems?: OfferLineItem[];
    tentativeBooking?: {
      eventRoomId: number;
      date: string;
      startTime: string;
      endTime: string;
    } | null;
  }
): Promise<EventOffer> {
  const res = await axios.post<EventOffer>(`/events/${eventId}/offers`, body);
  return res.data;
}

export async function updateOffer(
  eventId: number,
  id: number,
  body: Parameters<typeof createOffer>[1]
): Promise<EventOffer> {
  const res = await axios.patch<EventOffer>(`/events/${eventId}/offers/${id}`, body);
  return res.data;
}

export async function sendOffer(eventId: number, id: number): Promise<EventOffer> {
  const res = await axios.post<EventOffer>(`/events/${eventId}/offers/${id}/send`);
  return res.data;
}

export async function cancelOffer(eventId: number, id: number): Promise<EventOffer> {
  const res = await axios.post<EventOffer>(`/events/${eventId}/offers/${id}/cancel`);
  return res.data;
}

// ---- Event documents (Task 3) ----

export interface EventDocumentSummary {
  id: number;
  type: 'offer' | 'confirmation';
  version: number;
  documentNumber: string | null;
  fileSizeBytes: number;
  language: string;
  generatedAt: string;
  sentAt: string | null;
  isCurrent: boolean;
}

export async function listEventDocuments(eventId: number): Promise<EventDocumentSummary[]> {
  const res = await axios.get<EventDocumentSummary[]>(`/events/${eventId}/documents`);
  return Array.isArray(res.data) ? res.data : [];
}

export function eventDocumentDownloadUrl(eventId: number, documentId: number): string {
  const base = axios.defaults.baseURL ?? '';
  return `${base}/events/${eventId}/documents/${documentId}/download`;
}

export async function downloadEventDocument(
  eventId: number,
  documentId: number
): Promise<Blob> {
  const res = await axios.get<Blob>(
    `/events/${eventId}/documents/${documentId}/download`,
    { responseType: 'blob' }
  );
  return res.data;
}

export async function previewOfferPdf(
  eventId: number,
  offerId: number,
  language?: string
): Promise<Blob> {
  const res = await axios.post<Blob>(
    `/events/${eventId}/documents/preview`,
    { offerId, language },
    { responseType: 'blob' }
  );
  return res.data;
}

// ---- Change requests (Task 3) ----

export interface ChangeRequestSummary {
  id: number;
  visibility: 'customer_facing' | 'internal_only';
  reason: string;
  fieldsChanged: unknown;
  sourceDocumentId: number | null;
  resultingDocumentId: number | null;
  customerNotifiedAt: string | null;
  createdAt: string;
}

export async function listChangeRequests(eventId: number): Promise<ChangeRequestSummary[]> {
  const res = await axios.get<ChangeRequestSummary[]>(`/events/${eventId}/change-requests`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function applyChangeRequest(
  eventId: number,
  body: {
    visibility: 'customer_facing' | 'internal_only';
    reason: string;
    offerId: number;
    updates: {
      validUntil?: string;
      currency?: string;
      notesForPartner?: string | null;
      internalNotes?: string | null;
      lineItems?: Array<{
        sortOrder?: number;
        itemType: 'event_room' | 'hotel_room' | 'service' | 'other';
        description: string;
        quantity: number;
        unitPriceCents: number;
        referenceId?: number | null;
      }>;
    };
  }
): Promise<{
  id: number;
  visibility: string;
  resultingDocumentId: number | null;
  customerNotifiedAt: string | null;
}> {
  const res = await axios.post(`/events/${eventId}/change-requests`, body);
  return res.data;
}

export async function changePassword(body: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ ok: boolean }> {
  const res = await axios.post('/auth/change-password', body);
  return res.data;
}

export async function setPreferredLanguage(language: 'hu' | 'en'): Promise<{ language: string }> {
  const res = await axios.post('/auth/preferences/language', { language });
  return res.data;
}

// ---- Event agenda items (Task 4 — dispo) ----

export async function listEventAgendaItems(eventId: number): Promise<EventAgendaItem[]> {
  const res = await axios.get<EventAgendaItem[]>(`/events/${eventId}/agenda-items`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventAgendaItem(
  eventId: number,
  body: {
    startTime: string; // 'YYYY-MM-DD HH:mm'
    endTime?: string | null;
    title: string;
    description?: string | null;
    responsibleRole?: EventOrganizerRole | null;
    sortOrder?: number;
  }
): Promise<EventAgendaItem> {
  const res = await axios.post<EventAgendaItem>(`/events/${eventId}/agenda-items`, body);
  return res.data;
}

export async function updateEventAgendaItem(
  eventId: number,
  itemId: number,
  body: Partial<{
    startTime: string;
    endTime: string | null;
    title: string;
    description: string | null;
    responsibleRole: EventOrganizerRole | null;
    sortOrder: number;
  }>
): Promise<EventAgendaItem> {
  const res = await axios.patch<EventAgendaItem>(`/events/${eventId}/agenda-items/${itemId}`, body);
  return res.data;
}

export async function deleteEventAgendaItem(eventId: number, itemId: number): Promise<void> {
  await axios.delete(`/events/${eventId}/agenda-items/${itemId}`);
}

export async function reorderEventAgendaItems(
  eventId: number,
  ids: number[]
): Promise<EventAgendaItem[]> {
  const res = await axios.post<EventAgendaItem[]>(`/events/${eventId}/agenda-items/reorder`, { ids });
  return Array.isArray(res.data) ? res.data : [];
}

// ---- Event dispo notes (Task 4 — dispo) ----

export async function listEventDispoNotes(
  eventId: number,
  targetRole?: EventOrganizerRole | null
): Promise<EventDispoNote[]> {
  const params: Record<string, string> = {};
  if (targetRole) params.targetRole = targetRole;
  const res = await axios.get<EventDispoNote[]>(`/events/${eventId}/dispo-notes`, { params });
  return Array.isArray(res.data) ? res.data : [];
}

export async function createEventDispoNote(
  eventId: number,
  body: {
    note: string;
    targetRole?: EventOrganizerRole | null;
  }
): Promise<EventDispoNote> {
  const res = await axios.post<EventDispoNote>(`/events/${eventId}/dispo-notes`, body);
  return res.data;
}

export async function updateEventDispoNote(
  eventId: number,
  noteId: number,
  body: Partial<{ note: string; targetRole: EventOrganizerRole | null }>
): Promise<EventDispoNote> {
  const res = await axios.patch<EventDispoNote>(`/events/${eventId}/dispo-notes/${noteId}`, body);
  return res.data;
}

export async function deleteEventDispoNote(eventId: number, noteId: number): Promise<void> {
  await axios.delete(`/events/${eventId}/dispo-notes/${noteId}`);
}

// ---- Dispo documents (Task 4 — dispo) ----

export async function listEventDispos(eventId: number): Promise<EventDispoSummary[]> {
  const res = await axios.get<EventDispoSummary[]>(`/events/${eventId}/dispos`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function regenerateEventDispo(
  eventId: number,
  language?: string
): Promise<EventDispoSummary> {
  const res = await axios.post<EventDispoSummary>(`/events/${eventId}/dispos/regenerate`, {
    language
  });
  return res.data;
}

export async function downloadEventDispo(eventId: number, dispoId: number): Promise<Blob> {
  const res = await axios.get<Blob>(`/events/${eventId}/dispos/${dispoId}/download`, {
    responseType: 'blob'
  });
  return res.data;
}
